import IconeAjuda from '@/global/components/IconeAjuda'
import Dialog from '@/global/guides/Dialog'
import { hasPermission } from '@/hooks/useBackendMenu'
import {
    CreateTrocaInicioPontoSchema,
    empresas as empresa_api,
    TrocaInicioPonto,
    trocaInicioPonto,
} from '@/service/api'
import { Alert } from '@mui/material'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import FormAlterarInicioPonto from '../atoms/forms/AlterarInicioPonto/FormAlterarInicioPonto'
import { useEditarEmpresaOutletContext } from './Editar'

export default function AlterarFechamentoPonto() {
    const { data: empresa } = useEditarEmpresaOutletContext()
    const { data: trocas = [], isFetched } = trocaInicioPonto.useFetch({
        queryParams: { empresaId: empresa.id },
        enabled: !!empresa.id,
    })
    const navigate = useNavigate()
    const trocaExistente = trocas.find(
        it => it.competencia === empresa.competenciaAtual,
    )
    const defaultValues =
        trocaExistente ||
        ({
            diaInicioPonto: empresa.diaInicioPonto,
            diaAnterior: (() => {
                const proximaTroca = trocas.find(it =>
                    dayjs(it.competencia, 'YYYY-MM').isAfter(
                        dayjs(empresa.competenciaAtual, 'YYYY-MM'),
                        'month',
                    ),
                )
                if (proximaTroca) return proximaTroca.diaAnterior
                return empresa.diaInicioPonto
            })(),
        } as TrocaInicioPonto)
    const { mutateAsync, isLoading } = trocaInicioPonto.mutations.useCreate({
        onSuccess: () => {
            toast.success('Dia de início do ponto alterado com sucesso')
        },
        onError: error => {
            toast.error(error.message)
        },
    })

    const canSee = hasPermission(['update', 'Empresas'])

    if (!empresa || !isFetched) return null

    return (
        <Dialog.Root
            loading={isLoading}
            open={canSee}
            schema={CreateTrocaInicioPontoSchema.omit({ empresaId: true })}
            defaultValues={{
                diaInicioPonto: defaultValues.diaInicioPonto,
                diaAnterior: defaultValues.diaAnterior,
                competencia: empresa.competenciaAtual,
                maximoDiasCompetencia: 31,
            }}
            onSubmit={async values => {
                return mutateAsync({ empresaId: empresa.id, ...values }).then(
                    () => {
                        empresa_api.invalidateQueries()
                        navigate('/cadastros/empresas')
                    },
                )
            }}
        >
            <Dialog.Title>Alterar dia de início do ponto</Dialog.Title>
            <Dialog.Content dividers>
                {!!trocaExistente && (
                    <Alert severity="warning" className="mb-4">
                        <span className="inline-flex items-center">
                            O dia de início do ponto foi alterado em{' '}
                            {dayjs
                                .utc(defaultValues.createdAt)
                                .format('DD/MM/YYYY')}
                            .
                            <IconeAjuda>
                                <div className="max-h-64 overflow-auto">
                                    <table className="border-collapse">
                                        <thead>
                                            <tr className="*:p-1 *:border rounded-t-sm">
                                                <th>Competência</th>
                                                <th>Dia de início do ponto</th>
                                                <th>Período da competência</th>
                                                <th>Data da troca</th>
                                            </tr>
                                        </thead>
                                        <tbody className="rounded-b-sm">
                                            {trocas.map(troca => (
                                                <tr
                                                    key={troca.id}
                                                    className="*:p-1 *:border"
                                                >
                                                    <td>
                                                        {troca.competencia
                                                            .split('-')
                                                            .reverse()
                                                            .join('/')}
                                                    </td>
                                                    <td>
                                                        {troca.diaInicioPonto}
                                                    </td>
                                                    <td>
                                                        {dayjs
                                                            .utc(
                                                                troca.dataInicial,
                                                            )
                                                            .format(
                                                                'DD/MM/YYYY',
                                                            ) +
                                                            ' - ' +
                                                            dayjs
                                                                .utc(
                                                                    troca.dataFinal,
                                                                )
                                                                .format(
                                                                    'DD/MM/YYYY',
                                                                )}
                                                    </td>
                                                    <td>
                                                        {dayjs
                                                            .utc(
                                                                troca.createdAt,
                                                            )
                                                            .format(
                                                                'DD/MM/YYYY',
                                                            )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </IconeAjuda>
                        </span>
                    </Alert>
                )}
                <FormAlterarInicioPonto trocasInicioPonto={trocas} />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton />
            </Dialog.Actions>
        </Dialog.Root>
    )
}
